import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import AudioPlayer from "../../utils/audioUtils";
import styles from "./VideoConsultReadiness.module.scss";

// How long the test ring stays audible. Long enough to be recognisable as the
// incoming-call sound, short enough not to become a nuisance. The stop does
// NOT wait for the permission prompt to be answered: that prompt can sit open
// for minutes, and the ring is not what is being confirmed by it.
const RING_TEST_MS = 1500;

const STATUS_ID = "video-consult-readiness-status";

type AudioResult = "started" | "blocked" | "failed";

const NOTIFICATION_COPY: Record<NotificationPermission, string> = {
  granted: "Obaveštenja: uključena. Da li ste videli test obaveštenje?",
  default: "Obaveštenja: zahtev nije potvrđen, pokušajte ponovo.",
  denied:
    "Obaveštenja: blokirana. Uključite ih u podešavanjima sajta (ikonica levo od adrese) i osvežite stranicu.",
};

const AUDIO_COPY: Record<AudioResult, string> = {
  started: "Zvuk: pustili smo kratko test zvono. Da li ste ga čuli?",
  blocked: "Zvuk: browser je blokirao puštanje zvuka na ovoj stranici.",
  failed: "Zvuk: zvučno obaveštenje nije moglo da se pusti.",
};

/**
 * One deliberate readiness action for the two channels an incoming call
 * arrives on. Both of them are activation-sensitive:
 *
 * - `Notification.requestPermission()` only shows a real prompt when it runs
 *   inside a user gesture (Firefox and Safari require it outright; Chrome
 *   demotes a gesture-less request to a quiet address-bar icon nobody
 *   notices), so a prompt fired from a mount timer is not a prompt at all.
 * - `play()` without activation is rejected by the autoplay policy, and a
 *   resolved `play()` is the only check a page has for audio. It is a weak
 *   one: a muted tab, a muted OS or a silent output device all resolve
 *   happily, which is why the pharmacist is asked to confirm what they
 *   actually heard rather than being shown a green check.
 *
 * Both calls are therefore started synchronously inside the click, before
 * anything is awaited, and this component is the ONLY place that asks for the
 * notification permission (call-time code just checks that it is granted).
 *
 * Shown only while the permission is not granted: a pharmacist who is already
 * set up is not nagged, and the ringtone still has its own "Pokreni zvuk"
 * recovery on the incoming-call alert.
 */
const VideoConsultReadiness: React.FC = () => {
  const audioPlayer = AudioPlayer.getInstance();
  const [dismissed, setDismissed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    null
  );
  const [audio, setAudio] = useState<AudioResult | null>(null);

  // A real incoming call owns the ringtone. If one starts while the test ring
  // is still running, the stop below must not reach it: killing the ring of an
  // actual call is precisely the failure this whole feature exists to prevent.
  const ringing = useSelector(
    (state: RootState) => state.videoConsult.playRingtone
  );
  const ringingRef = useRef(ringing);
  useEffect(() => {
    ringingRef.current = ringing;
  }, [ringing]);

  const stopTimer = useRef<number | null>(null);
  const testNotification = useRef<Notification | null>(null);
  // A check settling after unmount must not touch state.
  const disposed = useRef(false);

  const stopTestRing = useCallback(() => {
    stopTimer.current = null;
    if (ringingRef.current) return;
    audioPlayer.stopRingtone();
  }, [audioPlayer]);

  useEffect(
    () => () => {
      disposed.current = true;
      if (stopTimer.current !== null) {
        window.clearTimeout(stopTimer.current);
        stopTestRing();
      }
      testNotification.current?.close();
    },
    [stopTestRing]
  );

  const handleCheck = () => {
    if (typeof Notification === "undefined") return;
    setChecking(true);

    // A re-check must not inherit the previous run's stop, which would cut
    // this ring short.
    if (stopTimer.current !== null) window.clearTimeout(stopTimer.current);

    // No await before either of these two: they must run inside the gesture.
    const audioResult = audioPlayer.playRingtone();
    stopTimer.current = window.setTimeout(stopTestRing, RING_TEST_MS);

    let permissionResult: Promise<NotificationPermission>;
    try {
      permissionResult =
        Notification.permission === "default"
          ? Notification.requestPermission()
          : Promise.resolve(Notification.permission);
    } catch {
      // Older browsers expose only the callback form and throw on the
      // promise call; report the permission we can still read.
      permissionResult = Promise.resolve(Notification.permission);
    }

    Promise.all([audioResult, permissionResult])
      .then(([outcome, granted]) => {
        if (disposed.current) return;
        setChecking(false);
        setAudio(
          outcome === "started" || outcome === "already-playing"
            ? "started"
            : outcome === "blocked"
            ? "blocked"
            : "failed"
        );
        setPermission(granted);
        if (granted !== "granted") return;
        try {
          // Deliberately without `requireInteraction`: this one is a sample,
          // not a call, and the OS may dismiss it on its own schedule.
          testNotification.current = new Notification(
            "Obaveštenja su uključena",
            { body: "Ovako izgleda poziv na video konsultaciju." }
          );
        } catch {
          // Constructor can throw (e.g. insecure context); the permission
          // result is still worth reporting.
        }
      })
      .catch(() => {
        if (disposed.current) return;
        setChecking(false);
        setAudio("failed");
        setPermission(Notification.permission);
      });
  };

  if (typeof Notification === "undefined") return null;
  if (dismissed) return null;
  // `permission === null` means the check has not run yet: an already-granted
  // session sees nothing at all. Once it HAS run, the result stays on screen
  // until the pharmacist closes it, granted or not.
  if (permission === null && Notification.permission === "granted") return null;

  const enabled = permission === "granted";

  return (
    <div
      className={styles.readiness}
      role="region"
      aria-label="Priprema za dolazne konsultacije"
    >
      <div className={styles.text}>
        <div className={styles.title}>
          {enabled
            ? "Obaveštenja su uključena"
            : "Dolazne video konsultacije: uključite obaveštenja"}
        </div>
        {!enabled && (
          <div className={styles.body}>
            Bez obaveštenja poziv se ne vidi dok radite u drugom tabu.
          </div>
        )}
        <div id={STATUS_ID} role="status" className={styles.status}>
          {permission !== null && (
            <>
              <span>{NOTIFICATION_COPY[permission]}</span>
              {audio !== null && <span>{AUDIO_COPY[audio]}</span>}
            </>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.checkButton}
          onClick={handleCheck}
          disabled={checking}
          aria-describedby={STATUS_ID}
        >
          {checking ? "Proveravam..." : "Omogući obaveštenja i proveri zvuk"}
        </button>
        <button
          type="button"
          className={styles.dismissButton}
          onClick={() => setDismissed(true)}
          aria-label="Zatvori"
          title="Zatvori"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default VideoConsultReadiness;
